// lib/cashfree/videoTipPayment.ts
import { useState } from "react";
import {
  createSubscriptionPaymentOrder,
  verifyCashfreePayment,
} from "@/lib/cashfree/paymentService";
import { useToast } from "@/components/ui/use-toast";
import {
  getUserVideoTips,
  createVideoTip,
  updateVideoTipStatus,
} from "../appwrite/bizzApi";
import { paymentContactFromIdentifier } from "@/lib/cashfree/paymentContact";

// Enhanced tip payment creation function - OPTIMIZED
export async function createTipPaymentOrder(
  senderId: string,
  receiverId: string,
  videoId: string,
  amount: number,
  sender: {
    name: string;
    emailOrPhone: string;
    paymentPhone?: string;
  }
) {
  let tipRecord: any = null;

  try {
    // First create the tip record in database
    tipRecord = await createVideoTip({
      senderId,
      receiverId,
      videoId,
      amount,
      status: "pending",
    });

    // Create payment order with Cashfree
    const paymentContact = paymentContactFromIdentifier(sender.emailOrPhone, sender.paymentPhone);
    const orderResponse = await createSubscriptionPaymentOrder(
      senderId,
      amount,
      0, // No points for tips
      {
        id: senderId,
        name: sender.name,
        email: paymentContact.email,
        phone: paymentContact.phone,
      },
      "video_tip",
      `${window.location.origin}/payment/callback?source=video_tip&tip_id=${tipRecord.$id}&video_id=${videoId}&receiver_id=${receiverId}`
    );
    if (!orderResponse.success || !orderResponse.payment_session_id) {
      // ✅ Only update status once and throw error
      if (tipRecord) {
        await updateVideoTipStatus(tipRecord.$id, "failed");
      }
      throw new Error(
        orderResponse.error || "Failed to create payment session"
      );
    }

    // ✅ Removed redundant status update - tip is already 'pending'
    // We'll update it with order_id only if needed by your backend

    return {
      success: true,
      payment_session_id: orderResponse.payment_session_id,
      order_id: orderResponse.order_id,
      tip_id: tipRecord.$id,
    };
  } catch (error) {
    console.error("Error creating tip payment:", error);

    // ✅ Only update status if we have a tip record and haven't updated it yet
    if (tipRecord && error !== "Failed to create payment session") {
      try {
        await updateVideoTipStatus(tipRecord.$id, "failed");
      } catch (updateError) {
        console.error("Failed to update tip status:", updateError);
      }
    }

    throw error;
  }
}

// Hook for handling video tip payments - FULLY OPTIMIZED
export const useVideoTipPayment = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [lastPaymentTime, setLastPaymentTime] = useState(0);
  const { toast } = useToast();

  const handleTipPayment = async (
    senderId: string,
    receiverId: string,
    videoId: string,
    amount: number,
    sender: { name: string; emailOrPhone: string; paymentPhone?: string },
    onSuccess?: () => void
  ) => {
    // ✅ Add debouncing protection
    const now = Date.now();
    if (now - lastPaymentTime < 3000) {
      // 3 second cooldown
      toast({
        title: "Please wait",
        description: "Please wait a moment before trying again",
        variant: "destructive",
      });
      return;
    }

    if (isProcessing) {
      console.log("Payment already in progress, ignoring click");
      return;
    }

    setIsProcessing(true);
    setLastPaymentTime(now);

    // ✅ Add status update protection
    let tipStatusUpdated = false;

    const updateTipStatusOnce = async (
      tipId: string,
      status: "completed" | "failed" | "pending",
      paymentId?: string
    ) => {
      if (tipStatusUpdated) {
        console.log("Tip status already updated, skipping");
        return;
      }

      try {
        await updateVideoTipStatus(tipId, status, paymentId);
        tipStatusUpdated = true;
        console.log(`Tip status updated to: ${status}`);
      } catch (error) {
        console.error("Failed to update tip status:", error);
      }
    };

    try {
      // Validate tip amount
      if (amount < 1) {
        throw new Error("Minimum tip amount is ₹1");
      }

      if (amount > 10000) {
        throw new Error("Maximum tip amount is ₹10,000");
      }

      // Create payment order
      const paymentOrder = await createTipPaymentOrder(
        senderId,
        receiverId,
        videoId,
        amount,
        sender
      );

      if (!paymentOrder.success) {
        throw new Error("Failed to create payment order");
      }

      const cashfreeOrderId = paymentOrder.order_id!;
      if (!cashfreeOrderId) {
        throw new Error("No order ID received from Cashfree");
      }

      // Initialize Cashfree payment
      const cashfree = new (window as any).Cashfree({
        mode: "production",
      });

      const checkoutOptions = {
        paymentSessionId: paymentOrder.payment_session_id,
        returnUrl: `${window.location.origin}/payment/callback?order_id=${paymentOrder.order_id}&source=video_tip&tip_id=${paymentOrder.tip_id}&video_id=${videoId}&receiver_id=${receiverId}`,
        redirectTarget: "_modal",
      };

      // Handle payment completion
      cashfree
        .checkout(checkoutOptions)
        .then(async (response: any) => {
          try {
            if (response.error) {
              throw new Error(response.error.message || "Payment failed");
            }

            // ✅ Only verify and update if we have valid payment details
            if (response.paymentDetails) {
              // 🎯 START VERIFICATION LOADER HERE
              setIsVerifying(true);

              // Show processing toast with loader indication
              toast({
                title: "Processing Payment...",
                description: "Please wait while we verify your payment",
              });

              // Verify payment on our backend
              const isValid = await verifyCashfreePayment({
                orderId: cashfreeOrderId,
                paymentId: response.paymentDetails.paymentId,
              });

              if (!isValid) {
                await updateTipStatusOnce(paymentOrder.tip_id!, "failed");
                throw new Error("Payment verification failed");
              }

              // ✅ Update tip status to completed (only once)
              await updateTipStatusOnce(
                paymentOrder.tip_id!,
                "completed",
                response.paymentDetails.paymentId
              );

              // 🎯 SUCCESS TOAST - LOADER WILL HIDE AFTER THIS
              toast({
                title: "Tip Sent Successfully!",
                description: `₹${amount} tip sent to the creator`,
              });

              onSuccess?.();
            } else {
              throw new Error("No payment details received");
            }
          } catch (error) {
            console.error("Tip payment processing failed:", error);

            // ✅ Update tip status to failed (only once)
            await updateTipStatusOnce(paymentOrder.tip_id!, "failed");

            toast({
              title: "Tip Payment Failed",
              description: "Please contact support if amount was deducted",
              variant: "destructive",
            });
          } finally {
            // 🎯 HIDE LOADER HERE - AFTER VERIFICATION COMPLETE
            setIsVerifying(false);
          }
        })
        .catch(async (error: any) => {
          console.error("Cashfree checkout error:", error);

          // ✅ Update tip status to failed (only once)
          await updateTipStatusOnce(paymentOrder.tip_id!, "failed");

          toast({
            title: "Payment Failed",
            description: error.message || "Payment could not be completed",
            variant: "destructive",
          });
        });
    } catch (error) {
      console.error("Tip payment initiation failed:", error);

      toast({
        title: "Tip Failed",
        description:
          error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    handleTipPayment,
    isProcessing,
    isVerifying,
  };
};

// Tip analytics and reporting - UNCHANGED
export async function getTipAnalytics(userId: string) {
  try {
    const tips = await getUserVideoTips(userId, "received");

    const analytics = {
      totalTips: tips.documents.length,
      totalAmount: tips.documents.reduce(
        (sum: number, tip: any) => sum + tip.amount,
        0
      ),
      averageTip: 0,
      topTippers: {} as Record<
        string,
        { name: string; amount: number; count: number }
      >,
      dailyBreakdown: {} as Record<string, { tips: number; amount: number }>,
      topVideos: {} as Record<
        string,
        { title: string; tips: number; amount: number }
      >,
    };

    if (analytics.totalTips > 0) {
      analytics.averageTip = analytics.totalAmount / analytics.totalTips;
    }

    // Process tip data for insights
    tips.documents.forEach((tip: any) => {
      // Top tippers
      if (!analytics.topTippers[tip.senderId]) {
        analytics.topTippers[tip.senderId] = {
          name: "",
          amount: 0,
          count: 0,
        };
      }
      analytics.topTippers[tip.senderId].amount += tip.amount;
      analytics.topTippers[tip.senderId].count += 1;

      // Daily breakdown
      const day = new Date(tip.createdAt).toISOString().split("T")[0];
      if (!analytics.dailyBreakdown[day]) {
        analytics.dailyBreakdown[day] = { tips: 0, amount: 0 };
      }
      analytics.dailyBreakdown[day].tips += 1;
      analytics.dailyBreakdown[day].amount += tip.amount;

      // Top videos
      if (!analytics.topVideos[tip.videoId]) {
        analytics.topVideos[tip.videoId] = {
          title: "",
          tips: 0,
          amount: 0,
        };
      }
      analytics.topVideos[tip.videoId].tips += 1;
      analytics.topVideos[tip.videoId].amount += tip.amount;
    });

    return analytics;
  } catch (error) {
    console.error("Error getting tip analytics:", error);
    throw error;
  }
}
